import { useRef } from "react";
import {
  PaperPlaneRightIcon as PaperPlaneRight,
  PaperclipIcon as Paperclip,
  XIcon as X,
  FileCodeIcon as FileCode,
} from "@phosphor-icons/react";

const ACCEPT_ATTR =
  ".rpgle,.rpg,.sqlrpgle,.clle,.cl,.clp,.dds,.pf,.lf,.sql,.ddl,.txt,.json,.xml,.yaml,.yml,.md";

export default function ChatComposer({
  agent,
  input,
  setInput,
  attachments,
  removeAttachment,
  sending,
  isDragging,
  onSend,
  onPickedFiles,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  const fileInputRef = useRef(null);
  const isCodeAnalyst = agent?.id === "code-analyst";

  const handleAttachFile = async (e) => {
    const files = Array.from(e.target.files || []);
    await onPickedFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div
      className="border-t border-[var(--ml-border)] p-4 lg:p-5 bg-[var(--ml-surface)]/40 relative"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="chat-composer"
    >
      {isDragging && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none border-2 border-dashed border-[var(--ml-primary)] bg-[var(--ml-primary)]/5"
          data-testid="drop-overlay"
        >
          <div className="ml-mono text-[12px] uppercase tracking-widest text-[var(--ml-primary)] bg-white px-4 py-2 border border-[var(--ml-primary)]/40">
            Drop to attach · RPG · CL · DDS · SQL · text
          </div>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="attachments-list">
          {attachments.map((a, i) => (
            <span
              key={a.uid || `${a.name}-${i}`}
              className="inline-flex items-center gap-2 bg-white border border-[var(--ml-border)] px-2 py-1 text-xs"
            >
              <FileCode size={14} weight="bold" color="#0f62fe" />
              <span className="ml-mono">{a.name}</span>
              <button
                onClick={() => removeAttachment(i)}
                className="text-[var(--ml-text-secondary)] hover:text-[var(--ml-error)]"
                data-testid={`remove-attachment-${i}`}
              >
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            isCodeAnalyst
              ? "Paste RPG, CL, DDS, or SQL — or describe a code review request. (⌘/Ctrl+Enter to send)"
              : `Ask ${agent.name} anything... (⌘/Ctrl+Enter to send)`
          }
          rows={3}
          className="ml-textarea resize-none flex-1"
          data-testid="chat-input"
          disabled={sending}
        />
        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleAttachFile}
            accept={ACCEPT_ATTR}
            data-testid="file-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="ml-btn-secondary"
            title="Attach source file"
            data-testid="attach-file-btn"
            disabled={sending}
          >
            <Paperclip size={16} />
          </button>
          <button
            onClick={onSend}
            disabled={sending || (!input.trim() && attachments.length === 0)}
            className="ml-btn-primary"
            data-testid="send-message-btn"
          >
            <PaperPlaneRight size={16} weight="bold" />
          </button>
        </div>
      </div>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-2">
        {isCodeAnalyst
          ? "Supports RPG · CL · DDS · DDL · SQL · text · drag-and-drop to attach"
          : "Markdown · code blocks · tables · drag-and-drop to attach"}{" "}
        · ⌘/Ctrl + Enter to send
      </div>
    </div>
  );
}
